import React, { useContext } from "react";
import { Grid, Typography, Slider } from "@material-ui/core";
import { useForm, Controller } from "react-hook-form";
import { debounce } from "throttle-debounce";
import RecurrentPaymentContext from "../../../../../../../infrastructure/Contexts/components/data/RecurrentPaymentsWrapper/context";
import RecurrentPaymentActions from "../../../../../../../infrastructure/Contexts/components/actions/RecurrentPaymentActionsWrapper/context";
import CompanyContext from "../../../../../../../infrastructure/Contexts/components/data/CompanyWrapper/context";
import { ReactComponent as SliderArrows } from "../../../../../../../assets/icons/slider-arrows.svg";
import CobeeButton from "../../../../../CobeeButton";
import useStyles from "./styles";
import { translate as t } from "../../../../../../../infrastructure/translate/localization";
import { MonetaryAmount } from "../../../../../../domain/monetary-amount";
import CobeeTextField from "../../../../../CobeeTextField";

export default function AcceptEducationStep1(): JSX.Element {
  const { recurrentPaymentDetail } = useContext(RecurrentPaymentContext);
  const { approveHHRREducation, approveHHRREducationIsLoading } = useContext(
    RecurrentPaymentActions
  );
  const { company } = useContext(CompanyContext);
  const { name: companyName } = company;

  const {
    title,
    container,
    containerSlider,
    amountContainerLeft,
    amountContainerRight,
    amountNameContainer,
    name,
    amountStyle,
    amountText,
    buttonContainer,
    errorMessage,
  } = useStyles();

  const { amount, id, employee } = recurrentPaymentDetail;
  const totalAmount = MonetaryAmount.ofCurrency({
    amountInCents: amount.amountInCents,
    currency: amount.currency,
  }).toUnitary();

  const { setValue, control, errors, clearErrors, handleSubmit } = useForm({
    defaultValues: {
      company: 0,
      employee: totalAmount,
      slider: totalAmount,
    },
  });

  const CobeeSlider = (props) => {
    return (
      <span {...props}>
        <SliderArrows />
      </span>
    );
  };

  const handleChange = debounce(100, false, (newValue) => {
    setValue("employee", newValue);
    setValue("company", totalAmount - newValue);
    clearErrors();
  });

  const onSubmit = ({ employee: employeeAmount, company: companyAmount }) => {
    approveHHRREducation({
      requestId: id,
      employeeAmount,
      companyAmount,
    });
  };

  const getParseValue = (event) => {
    const {
      target: { value },
    } = event;
    let parsedValue =
      value !== "" ? Number(parseFloat(value).toFixed(2)) : value;
    if (parsedValue > totalAmount) {
      parsedValue = totalAmount;
    }
    return parsedValue;
  };

  const updateValues = (parsedValue, field) => {
    setValue("slider", parsedValue);
    setValue(field, Number((totalAmount - parsedValue).toFixed(2)));
    clearErrors();
  };

  return (
    <Grid>
      <Typography className={title}>
        {t(
          "recurrent-payments-section.how-do-you-want-the-payment-of-this-training-to-be-made-for",
          {
            name: employee.name,
            surname: employee.surname,
          }
        )}
      </Typography>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container className={container}>
          <Grid className={amountContainerLeft}>
            <Grid
              container
              justifyContent="center"
              alignItems="center"
              direction="column"
              className={amountNameContainer}
            >
              <Typography align="center" className={name}>
                {companyName}
              </Typography>
            </Grid>
            <Typography align="center" className={amountText}>
              {`${t("common-messages.will-pay")}...`}
            </Typography>
            <Grid container alignItems="center">
              <Controller
                render={({ onChange, value }) => (
                  <CobeeTextField
                    type="number"
                    minWidth={118}
                    min={0}
                    max={totalAmount}
                    variant="outlined"
                    styleType="default"
                    step="0.01"
                    value={value}
                    onChange={(e) => {
                      const parsedValue = getParseValue(e);
                      onChange(parsedValue);
                      if (parsedValue !== "") {
                        updateValues(parsedValue, "employee");
                      }
                    }}
                    error={"company" in errors}
                  />
                )}
                name="company"
                control={control}
                rules={{ required: true }}
              />
              <Typography className={amountStyle}>€</Typography>
            </Grid>
            {errors.company && (
              <Typography className={errorMessage}>
                {t("common-messages.field-is-required")}
              </Typography>
            )}
          </Grid>
          <Grid container alignItems="center" className={containerSlider}>
            <Controller
              render={({ onChange, value }) => (
                <Slider
                  value={value}
                  min={0}
                  max={totalAmount}
                  onChange={(_event, newValue) => {
                    onChange(newValue);
                    handleChange(newValue);
                  }}
                  ThumbComponent={CobeeSlider}
                />
              )}
              name="slider"
              control={control}
            />
          </Grid>
          <Grid className={amountContainerRight}>
            <Grid
              container
              justifyContent="center"
              alignItems="center"
              direction="column"
              className={amountNameContainer}
            >
              <Typography variant="body2" className={name} align="center">
                {`${employee.name} ${employee.surname}`}
              </Typography>
            </Grid>
            <Typography variant="body2" align="center" className={amountText}>
              {`${t("common-messages.will-pay")}...`}
            </Typography>
            <Grid container alignItems="center">
              <Controller
                render={({ onChange, value }) => (
                  <CobeeTextField
                    type="number"
                    minWidth={118}
                    min={0}
                    max={totalAmount}
                    variant="outlined"
                    styleType="default"
                    step="0.01"
                    onChange={(e) => {
                      const parsedValue = getParseValue(e);
                      onChange(parsedValue);
                      if (parsedValue !== "") {
                        updateValues(parsedValue, "company");
                      }
                    }}
                    value={value}
                    error={"employee" in errors}
                  />
                )}
                name="employee"
                control={control}
                rules={{ required: true }}
              />
              <Typography className={amountStyle}>€</Typography>
            </Grid>
            {errors.employee && (
              <Typography className={errorMessage}>
                {t("common-messages.field-is-required")}
              </Typography>
            )}
          </Grid>
        </Grid>

        <Grid container justifyContent="flex-end" className={buttonContainer}>
          <CobeeButton type="submit" isLoading={approveHHRREducationIsLoading}>
            {t("actions.accept-request")}
          </CobeeButton>
        </Grid>
      </form>
    </Grid>
  );
}
